import { Document, Packer, Paragraph, TextRun, Table, TableCell, TableRow, WidthType, AlignmentType, HeadingLevel, ImageRun } from 'docx';
import { saveAs } from 'file-saver';
import logoImage from '../assets/logo.png';

const COLORS = {
    gering: '10B981',
    mittel: 'F59E0B',
    hoch: 'EF4444',
    sehr_hoch: 'DC2626',
    primary: 'D4AF37',
};

const LABELS = {
    gering: 'Gering',
    mittel: 'Mittel',
    hoch: 'Hoch',
    sehr_hoch: 'Sehr Hoch',
};

const STATUS_LABELS = {
    offen: 'Offen',
    in_bearbeitung: 'In Bearbeitung',
    geschlossen: 'Geschlossen',
};

const MASSNAHMEN_STATUS_LABELS = {
    geplant: 'Geplant',
    in_umsetzung: 'In Umsetzung',
    abgeschlossen: 'Abgeschlossen',
};

const PRIORITAET_LABELS = {
    niedrig: 'Niedrig',
    mittel: 'Mittel',
    hoch: 'Hoch',
    kritisch: 'Kritisch',
};

const createCoverPage = async (mandantName, currentDate) => {
    const paragraphs = [];

    try {
        const response = await fetch(logoImage);
        const arrayBuffer = await response.arrayBuffer();

        paragraphs.push(
            new Paragraph({
                children: [
                    new ImageRun({
                        data: arrayBuffer,
                        transformation: {
                            width: 200,
                            height: 200,
                        },
                    }),
                ],
                alignment: AlignmentType.CENTER,
                spacing: { after: 600 },
            })
        );
    } catch (error) {
        console.error('Logo-Fehler:', error);
        paragraphs.push(
            new Paragraph({
                children: [
                    new TextRun({
                        text: 'REGENTRA',
                        size: 48,
                        bold: true,
                        color: 'D4AF37',
                    }),
                ],
                alignment: AlignmentType.CENTER,
                spacing: { after: 600 },
            })
        );
    }

    paragraphs.push(
        new Paragraph({
            text: 'Risikomanagement',
            heading: HeadingLevel.HEADING_1,
            alignment: AlignmentType.CENTER,
            spacing: { before: 400, after: 400 },
        })
    );

    paragraphs.push(
        new Paragraph({
            text: 'Risiken mit Bewertung und Maßnahmen',
            alignment: AlignmentType.CENTER,
            spacing: { after: 1200 },
        })
    );

    paragraphs.push(
        new Paragraph({
            children: [
                new TextRun({
                    text: '─────────────────────────────────',
                    color: 'E5E7EB',
                }),
            ],
            alignment: AlignmentType.CENTER,
            spacing: { after: 400 },
        })
    );

    paragraphs.push(
        new Paragraph({
            children: [
                new TextRun({
                    text: 'Mandant: ',
                    bold: true,
                    size: 24,
                }),
                new TextRun({
                    text: mandantName,
                    size: 24,
                }),
            ],
            alignment: AlignmentType.CENTER,
            spacing: { after: 200 },
        })
    );

    paragraphs.push(
        new Paragraph({
            children: [
                new TextRun({
                    text: 'Erstellt am: ',
                    bold: true,
                    size: 24,
                }),
                new TextRun({
                    text: currentDate,
                    size: 24,
                }),
            ],
            alignment: AlignmentType.CENTER,
            spacing: { after: 400 },
        })
    );

    paragraphs.push(
        new Paragraph({
            children: [
                new TextRun({
                    text: 'Vertraulich',
                    italic: true,
                    size: 20,
                    color: '6B7280',
                }),
            ],
            alignment: AlignmentType.CENTER,
            spacing: { before: 800, after: 400 },
        })
    );

    paragraphs.push(
        new Paragraph({
            text: '',
            pageBreakBefore: true,
        })
    );

    return paragraphs;
};

const createExecutiveSummary = (risiken, massnahmen) => {
    const gesamtRisiken = risiken.length;
    const kritischeRisiken = risiken.filter(r => r.risikostufe === 'sehr_hoch' || r.risikostufe === 'hoch').length;
    const offeneRisiken = risiken.filter(r => r.status === 'offen').length;
    const gesamtMassnahmen = massnahmen.length;
    const abgeschlosseneMassnahmen = massnahmen.filter(m => m.status === 'abgeschlossen').length;
    const kritischeMassnahmen = massnahmen.filter(m => m.prioritaet === 'kritisch' || m.prioritaet === 'hoch').length;

    return [
        new Paragraph({
            text: 'Management Summary',
            heading: HeadingLevel.HEADING_1,
            spacing: { before: 400, after: 200 },
        }),

        new Paragraph({
            text: 'Überblick',
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 200, after: 200 },
        }),

        new Paragraph({
            text: `Diese Risikoanalyse umfasst ${gesamtRisiken} identifizierte Risiken, wovon ${kritischeRisiken} als kritisch eingestuft wurden. Zur Risikominimierung wurden ${gesamtMassnahmen} Maßnahmen definiert.`,
            spacing: { after: 400 },
        }),

        createSummaryTable(gesamtRisiken, kritischeRisiken, offeneRisiken, gesamtMassnahmen, abgeschlosseneMassnahmen, kritischeMassnahmen),

        new Paragraph({
            text: '',
            pageBreakBefore: true,
        }),
    ];
};

const createSummaryTable = (gesamt, kritisch, offen, massnahmen, abgeschlossen, kritischeMassnahmen) => {
    return new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        rows: [
            new TableRow({
                children: [
                    new TableCell({
                        children: [new Paragraph({ text: 'Kennzahl', bold: true })],
                        shading: { fill: 'F3F4F6' },
                    }),
                    new TableCell({
                        children: [new Paragraph({ text: 'Wert', bold: true })],
                        shading: { fill: 'F3F4F6' },
                    }),
                ],
            }),
            new TableRow({
                children: [
                    new TableCell({ children: [new Paragraph('Identifizierte Risiken')] }),
                    new TableCell({ children: [new Paragraph(gesamt.toString())] }),
                ],
            }),
            new TableRow({
                children: [
                    new TableCell({
                        children: [new Paragraph({ text: 'Kritische Risiken', bold: true })],
                        shading: { fill: kritisch > 0 ? 'FEF3C7' : 'FFFFFF' },
                    }),
                    new TableCell({
                        children: [new Paragraph({ text: kritisch.toString(), bold: true })],
                        shading: { fill: kritisch > 0 ? 'FEF3C7' : 'FFFFFF' },
                    }),
                ],
            }),
            new TableRow({
                children: [
                    new TableCell({ children: [new Paragraph('Offene Risiken')] }),
                    new TableCell({ children: [new Paragraph(offen.toString())] }),
                ],
            }),
            new TableRow({
                children: [
                    new TableCell({ children: [new Paragraph('Definierte Maßnahmen')] }),
                    new TableCell({ children: [new Paragraph(massnahmen.toString())] }),
                ],
            }),
            new TableRow({
                children: [
                    new TableCell({ children: [new Paragraph('Abgeschlossene Maßnahmen')] }),
                    new TableCell({ children: [new Paragraph(abgeschlossen.toString())] }),
                ],
            }),
            new TableRow({
                children: [
                    new TableCell({
                        children: [new Paragraph({ text: 'Kritische Maßnahmen', bold: true })],
                        shading: { fill: kritischeMassnahmen > 0 ? 'FEF3C7' : 'FFFFFF' },
                    }),
                    new TableCell({
                        children: [new Paragraph({ text: kritischeMassnahmen.toString(), bold: true })],
                        shading: { fill: kritischeMassnahmen > 0 ? 'FEF3C7' : 'FFFFFF' },
                    }),
                ],
            }),
        ],
    });
};

const createRisikenSection = (risiken, massnahmen) => {
    const sections = [
        new Paragraph({
            text: 'Risikoanalyse',
            heading: HeadingLevel.HEADING_1,
            spacing: { before: 400, after: 200 },
        }),
    ];

    if (risiken.length === 0) {
        sections.push(
            new Paragraph({
                text: 'Keine Risiken erfasst.',
                spacing: { after: 200 },
            })
        );
        return sections;
    }

    const sortedRisiken = [...risiken].sort((a, b) => b.risikoscore - a.risikoscore);

    sortedRisiken.forEach((risiko, index) => {
        const risikoMassnahmen = massnahmen.filter(m => m.risiko_id === risiko.risiko_id);

        sections.push(
            new Paragraph({
                text: `${index + 1}. ${risiko.risiko_bezeichnung}`,
                heading: HeadingLevel.HEADING_2,
                spacing: { before: 300, after: 200 },
            })
        );

        if (risiko.risiko_beschreibung) {
            sections.push(
                new Paragraph({
                    text: risiko.risiko_beschreibung,
                    spacing: { after: 200 },
                })
            );
        }

        sections.push(createRisikoMetaTable(risiko));

        if (risikoMassnahmen.length > 0) {
            sections.push(
                new Paragraph({
                    text: 'Maßnahmen',
                    heading: HeadingLevel.HEADING_3,
                    spacing: { before: 200, after: 100 },
                })
            );
            sections.push(createMassnahmenTable(risikoMassnahmen));
        } else {
            sections.push(
                new Paragraph({
                    text: 'Keine Maßnahmen definiert.',
                    spacing: { before: 200, after: 100 },
                    italics: true,
                })
            );
        }

        sections.push(
            new Paragraph({
                text: '',
                spacing: { after: 400 },
            })
        );
    });

    sections.push(
        new Paragraph({
            text: '',
            pageBreakBefore: true,
        })
    );

    return sections;
};

const createRisikoMetaTable = (risiko) => {
    const rows = [
        new TableRow({
            children: [
                new TableCell({
                    children: [new Paragraph({ text: 'Kategorie', bold: true })],
                    width: { size: 30, type: WidthType.PERCENTAGE },
                }),
                new TableCell({
                    children: [new Paragraph(risiko.risiko_kategorie || '-')],
                    width: { size: 70, type: WidthType.PERCENTAGE },
                }),
            ],
        }),
        new TableRow({
            children: [
                new TableCell({ children: [new Paragraph({ text: 'Bereich', bold: true })] }),
                new TableCell({ children: [new Paragraph(risiko.risiko_bereich || '-')] }),
            ],
        }),
        new TableRow({
            children: [
                new TableCell({ children: [new Paragraph({ text: 'Schadenshöhe', bold: true })] }),
                new TableCell({ children: [new Paragraph(`${risiko.schadenshoehe}/4`)] }),
            ],
        }),
        new TableRow({
            children: [
                new TableCell({ children: [new Paragraph({ text: 'Eintrittswahrscheinlichkeit', bold: true })] }),
                new TableCell({ children: [new Paragraph(`${risiko.eintrittswahrscheinlichkeit}/4`)] }),
            ],
        }),
        new TableRow({
            children: [
                new TableCell({ children: [new Paragraph({ text: 'Risikoscore', bold: true })] }),
                new TableCell({
                    children: [new Paragraph({ text: `${risiko.risikoscore}/16`, bold: true })],
                    shading: { fill: COLORS[risiko.risikostufe] || 'FFFFFF' },
                }),
            ],
        }),
        new TableRow({
            children: [
                new TableCell({ children: [new Paragraph({ text: 'Risikostufe', bold: true })] }),
                new TableCell({
                    children: [new Paragraph({ text: LABELS[risiko.risikostufe] || risiko.risikostufe, bold: true })],
                    shading: { fill: COLORS[risiko.risikostufe] || 'FFFFFF' },
                }),
            ],
        }),
        new TableRow({
            children: [
                new TableCell({ children: [new Paragraph({ text: 'Behandlungsstatus', bold: true })] }),
                new TableCell({ children: [new Paragraph(risiko.behandlungsstatus || '-')] }),
            ],
        }),
    ];

    if (risiko.verantwortlicher) {
        rows.push(
            new TableRow({
                children: [
                    new TableCell({ children: [new Paragraph({ text: 'Verantwortlich', bold: true })] }),
                    new TableCell({ children: [new Paragraph(risiko.verantwortlicher)] }),
                ],
            })
        );
    }

    return new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        rows: rows,
        margins: {
            top: 100,
            bottom: 100,
            left: 100,
            right: 100,
        },
    });
};

const createMassnahmenTable = (massnahmen) => {
    const rows = [
        new TableRow({
            children: [
                new TableCell({
                    children: [new Paragraph({ text: 'Maßnahme', bold: true })],
                    shading: { fill: 'F3F4F6' },
                    width: { size: 35, type: WidthType.PERCENTAGE },
                }),
                new TableCell({
                    children: [new Paragraph({ text: 'Typ', bold: true })],
                    shading: { fill: 'F3F4F6' },
                    width: { size: 15, type: WidthType.PERCENTAGE },
                }),
                new TableCell({
                    children: [new Paragraph({ text: 'Priorität', bold: true })],
                    shading: { fill: 'F3F4F6' },
                    width: { size: 15, type: WidthType.PERCENTAGE },
                }),
                new TableCell({
                    children: [new Paragraph({ text: 'Status', bold: true })],
                    shading: { fill: 'F3F4F6' },
                    width: { size: 15, type: WidthType.PERCENTAGE },
                }),
                new TableCell({
                    children: [new Paragraph({ text: 'Verantwortlich', bold: true })],
                    shading: { fill: 'F3F4F6' },
                    width: { size: 20, type: WidthType.PERCENTAGE },
                }),
            ],
        }),
    ];

    massnahmen.forEach(massnahme => {
        rows.push(
            new TableRow({
                children: [
                    new TableCell({
                        children: [
                            new Paragraph({ text: massnahme.massnahme_titel, bold: true }),
                            new Paragraph({
                                text: massnahme.massnahme_beschreibung || '',
                                spacing: { before: 100 }
                            }),
                        ],
                    }),
                    new TableCell({ children: [new Paragraph(massnahme.massnahme_typ || '-')] }),
                    new TableCell({
                        children: [new Paragraph(PRIORITAET_LABELS[massnahme.prioritaet] || massnahme.prioritaet || '-')],
                        shading: { fill: massnahme.prioritaet === 'kritisch' || massnahme.prioritaet === 'hoch' ? 'FEF3C7' : 'FFFFFF' },
                    }),
                    new TableCell({
                        children: [new Paragraph(MASSNAHMEN_STATUS_LABELS[massnahme.status] || massnahme.status || '-')],
                    }),
                    new TableCell({ children: [new Paragraph(massnahme.verantwortlicher || '-')] }),
                ],
            })
        );
    });

    return new Table({
        width: { size: 100, type: WidthType.PERCENTAGE },
        rows: rows,
    });
};

export const exportRisikomanagementToWord = async (data) => {
    const { risiken, massnahmen, mandantName } = data;
    const currentDate = new Date().toLocaleDateString('de-DE', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });

    const coverPage = await createCoverPage(mandantName, currentDate);

    const sections = [
        ...coverPage,
        ...createExecutiveSummary(risiken, massnahmen),
        ...createRisikenSection(risiken, massnahmen),
    ];

    const doc = new Document({
        creator: 'Regentra',
        title: `Risikomanagement - ${mandantName}`,
        description: 'Risiken mit Bewertung und Maßnahmen',
        sections: [
            {
                properties: {},
                children: sections,
            },
        ],
    });

    const blob = await Packer.toBlob(doc);
    const fileName = `Risikomanagement_${mandantName}_${new Date().toISOString().split('T')[0]}.docx`;
    saveAs(blob, fileName);
};
